/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { Animate, BlogCardProps, BlogCards, Wrap } from "@components/elements";
import { CosmosPagination } from "@components/elements/molecules/pagination/CosmosPagination";
import { BlogCardFragment, ResearchCardFragment } from "@cosmos/hygraph-schema";
import { css } from "@panda/css";
import { VStack } from "@panda/jsx";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useMessages } from "@epic-mw/i18n";
import { getResearchCardFragment } from "./research-card.utils";

export interface ResearchCardOrganismProps {
	items?: ResearchCardFragment[];
	enableAnimate?: boolean;
	disablePaddingBlockEnd?: boolean;
	pageSize?: number;
	enablePagination?: boolean;
}

const cardRowStyles = css({
	"&.blog-cards": {
		w: "full",
	},

	"&.blog-cards .cosmos-text.blog-card-title": {
		"& em": {
			fontWeight: "normal",
		},
	},

	"&.blog-cards .cosmos-text.blog-card-desc": {
		textStyle: "uiMD",
		lineClamp: "4",
	},
});

export const ResearchCardOrganism = ({
	items = [],
	enableAnimate = true,
	pageSize = 9,
	enablePagination = true,
}: ResearchCardOrganismProps): React.JSX.Element | null => {
	const [currentPage, setCurrentPage] = useState(1);
	const messages = useMessages();
	const publicationLabel = messages["epic.cosmos.publication"];

	const researchCards: BlogCardFragment[] = useMemo(() => {
		const resCards: BlogCardProps[] = items.map((item) => {
			return getResearchCardFragment(item, publicationLabel);
		});
		resCards.sort((a, b) => new Date(b.date || "").getTime() - new Date(a.date || "").getTime());
		return resCards;
	}, [items, publicationLabel]);

	const paginatedCards = useMemo(() => {
		if (!enablePagination) return researchCards;
		const start = (currentPage - 1) * pageSize;
		const end = start + pageSize;
		return researchCards.slice(start, end);
	}, [researchCards, currentPage, pageSize, enablePagination]);

	useEffect(() => {
		if (enablePagination && researchCards.length && (currentPage - 1) * pageSize >= researchCards.length) {
			setCurrentPage(1);
		}
	}, [researchCards.length, currentPage, pageSize, enablePagination]);

	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	if (!items || !items.length) return null;

	const rows = [];
	if (paginatedCards.length) {
		rows.push(
			<Wrap
				key="research-cards-animate-wrap"
				condition={enableAnimate}
				wrap={(children) => (
					<Animate delay={0.75} type="slide" customCss={css.raw({ flexBasis: "50%" })}>
						{children}
					</Animate>
				)}
			>
				<BlogCards
					key="research-cards"
					className={cardRowStyles}
					cards={paginatedCards}
					direction="column"
					rowSizeOverride={3}
				/>
			</Wrap>,
		);
	}

	return (
		<VStack alignItems="stretch" paddingBlock="eds.dimension.48" gap="eds.dimension.48">
			<VStack alignItems="flex-start" gap="eds.dimension.16">
				{rows}
			</VStack>
			{enablePagination && researchCards.length > pageSize && (
				<CosmosPagination
					current={currentPage}
					pageSize={pageSize}
					total={researchCards.length}
					handlePageChange={handlePageChange}
				/>
			)}
		</VStack>
	);
};
